import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Handshake } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import FileDrop from "@/components/site/FileDrop";

const inputCls =
  "w-full bg-[#F3EDE3] border border-[#DCD3C3] px-4 py-3.5 text-sm text-[#0A0A0A] placeholder-[#a79f90] focus:outline-none focus:border-[#B38728] transition-colors";

export default function OfferForm() {
  const [form, setForm] = useState({ name: "", email: "", item_title: "", asking_price: "", trade_preference: "Cash", condition_notes: "", reference: "" });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      await api.post("/offers", fd);
      toast.success("Offer sent — we'll be in touch within 24 hours.");
      setForm({ name: "", email: "", item_title: "", asking_price: "", trade_preference: "Cash", condition_notes: "", reference: "" });
      setFiles([]);
    } catch (err) {
      toast.error(formatApiError(err, "Could not send your offer"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="trade" className="py-24 md:py-32" data-testid="offer-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl"
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#B38728] flex items-center gap-3">
            <span className="inline-block w-10 h-px bg-[#B38728]" /> 04 — Sell & Trade
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight mt-5">Send us your offers</h2>
          <p className="text-[#5C564C] text-sm md:text-base leading-relaxed mt-5">
            Looking to cash out or trade up? Tell us what you have and your asking price.
            We pay instantly or offer store credit with a trade bonus — your call.
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 bg-white border border-[#DCD3C3] p-8 md:p-10 grid lg:grid-cols-2 gap-8"
          data-testid="offer-form"
        >
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Name *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="offer-name-input" className={`mt-2 ${inputCls}`} placeholder="Your name" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Email *</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="offer-email-input" className={`mt-2 ${inputCls}`} placeholder="you@email.com" />
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Item / Collection Title *</label>
              <input required value={form.item_title} onChange={(e) => setForm({ ...form, item_title: e.target.value })} data-testid="offer-title-input" className={`mt-2 ${inputCls}`} placeholder="e.g. UCS Millennium Falcon 75192, sealed" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Desired Price (£)</label>
                <input value={form.asking_price} onChange={(e) => setForm({ ...form, asking_price: e.target.value })} data-testid="offer-price-input" className={`mt-2 ${inputCls}`} placeholder="e.g. 600" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Preference</label>
                <select value={form.trade_preference} onChange={(e) => setForm({ ...form, trade_preference: e.target.value })} data-testid="offer-preference-select" className={`mt-2 ${inputCls}`}>
                  <option>Cash</option>
                  <option>Store Credit (+ bonus)</option>
                  <option>Open to Both</option>
                </select>
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Condition Notes</label>
              <textarea rows={4} value={form.condition_notes} onChange={(e) => setForm({ ...form, condition_notes: e.target.value })} data-testid="offer-condition-input" className={`mt-2 ${inputCls}`} placeholder="Box condition, completeness, any wear…" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C]">Valuation Reference (if we've valued this for you)</label>
              <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="offer-reference-input" className={`mt-2 ${inputCls}`} placeholder="e.g. SX-0001" />
            </div>
          </div>
          <div className="flex flex-col">
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5C564C] block mb-2">Photos / Videos</label>
            <FileDrop files={files} setFiles={setFiles} testId="offer-file-drop" />
            <div className="mt-auto pt-8">
              <button
                type="submit"
                disabled={sending}
                data-testid="offer-submit-button"
                className="w-full bg-[#0A0A0A] text-[#F3EDE3] py-4 font-mono text-xs uppercase tracking-[0.3em] hover:bg-[#B38728] hover:text-[#0A0A0A] transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Handshake className="w-4 h-4" />}
                {sending ? "Sending…" : "Send My Offer"}
              </button>
            </div>
          </div>
        </motion.form>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 border border-[#DCD3C3] bg-[#EAE2D5] p-8 md:p-10"
          data-testid="offer-how-it-works"
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#B38728]">How it works — start to finish</p>
          <ol className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-6 list-none">
            {[
              ["01", "Send your offer", "Photos, your asking price, cash or store credit — takes two minutes."],
              ["02", "We reply within 24h", "Accept, counter or a credit offer with a bonus — straight to your email with a reference number (looks like SX-0001)."],
              ["03", "Respond on the tracker", "Pop your reference and email into the Offer Tracker below to accept, decline or counter — no email chains."],
              ["04", "Post it to us", "Deal agreed? Postage instructions arrive automatically. Write your reference on a slip inside the parcel, then tap \"I've Posted It\" on the tracker."],
              ["05", "Get paid", "We verify your item on arrival. Cash lands by bank transfer; store credit arrives as a code that never expires — spend it all at once or bit by bit."],
            ].map(([n, title, text]) => (
              <li key={n} data-testid={`offer-step-${n}`}>
                <p className="font-mono text-xs text-[#B38728] tracking-[0.25em]">{n}</p>
                <p className="font-serif text-lg mt-2">{title}</p>
                <p className="text-xs text-[#5C564C] leading-relaxed mt-2">{text}</p>
              </li>
            ))}
          </ol>
        </motion.div>
      </div>
    </section>
  );
}
